import './app.scss';

import App from '@components/App';
import { Api } from '@config/api';
import { APP_ENVIRONMENT, NODE_ENV } from '@config/env';
import { Images } from '@config/images';
import { ConfigProvider, type TConfigContext } from '@context/ConfigContext';
import { renderRootForContainerId } from '@conversed/ui/src/helpers/render';
import {
  getConfigToUse,
  getConfigToUseFromQueryString,
} from '@services/configService';
import * as eventHandlerService from '@services/eventHandlerService';
import geoLocationService from '@services/geolocationService';
import { createHTTPInstance } from '@services/httpService';
import { sendLogMessage } from '@services/postMessageService';
import type { Config, Metadata } from '@type/configs';
import type { Location, LocationError } from '@type/location';

import * as Sentry from '@sentry/react';

Sentry.init({
  environment: APP_ENVIRONMENT,
  dsn: 'https://c8f6e7456ea3892f719728a26e840257@sentry.lynxsolutions.eu/16',
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
  ],
  // Performance Monitoring
  tracesSampleRate: 1.0, //  Capture 100% of the transactions
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: [/^https:\/\/.*\.conversed\.(ai|biz).*/],
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
});

const configToContext = (config: Config): TConfigContext => {
  const {
    scheme,
    options,
    options: {
      showWelcomeAsFirstMessage,
      greeting,
      icons,
      isSoundEnabled,
      revisionId,
      defaultState,
    },
    botId = '',
    configId,
  } = config;

  return {
    showWelcomeAsFirstMessage: showWelcomeAsFirstMessage ?? true,
    popupTimeout: (options.popupTimeout || 2) * 1000,
    welcomeMessage: options.welcomeMessage,
    waitingForTheBotResponseMessage: options.waitingForTheBotResponseMessage,
    enableWaitingForBotResponseBlocker:
      options.enableWaitingForBotResponseBlocker,
    notificationAIGeneratedAnswer: options.notificationAIGeneratedAnswer,
    messageIcon: icons.speechIcon || '',
    messagePlaceholder: options.messagePlaceholder,
    menuTemplate: options.menuTemplate || [],
    refParam: options.refParam,
    revisionId,
    useAttachment: options.useAttachment || false,
    language: options.language,
    isSoundEnabled: isSoundEnabled ?? true,
    bubbleProps: {
      dim: 48,
      icon:
        config && icons && icons.chatIcon ? icons.chatIcon : Images.bubbleIcon,
    },
    blockerConfig: {
      text: options?.blocker?.text || 'Updating conversation in another tab',
      buttonText: options?.blocker?.buttonText || 'Continue here',
    },
    headerConfig: {
      title: options.title || '',
      subtitle: options.subtitle || '',
      logoType: icons?.logo?.type ?? 'circle',
      logo: icons?.logo?.path ?? Images.cvLogo,
      headerType: options.headerType || 'normal',
    },
    scheme,
    greeting: {
      message: options.welcomeMessage,
      title: options.title,
      subtitle: options.subtitle,
      messageIcon: icons.speechIcon,
      avatarType: icons?.logo?.type ?? 'circle',
      avatarPath:
        icons?.logo?.path ?? `${process.env.PUBLIC_URL}${Images.cvLogo}`,
      headerType: options.headerType || 'normal',
      showCloseButton: options.showCloseButton !== false,
      ...greeting,
    },
    botId: NODE_ENV !== 'production' ? 'local' : botId,
    configId,
    defaultState: defaultState || 'closed',
  };
};

const render = (config: Config | null) => {
  const URL = Api.CONVERSED_SERVER;

  if (!config?.options || !config?.scheme) {
    sendLogMessage(
      'Initial config not available for webclient, exiting early',
      'error',
    );
    return;
  }

  const configToContextValue = configToContext(config);

  if (config?.isEnabled === false) {
    sendLogMessage('Bot configuration is not enabled, exiting early', 'error');
    return;
  }

  if (!configToContextValue?.botId) {
    sendLogMessage(
      'Failed to load webclient. Invalid config found: botId is required',
      'error',
    );
    return;
  }

  if (!configToContextValue?.configId) {
    sendLogMessage(
      'Failed to load webclient. Invalid config found: configId is required',
      'error',
    );
    return;
  }

  if (config?.shouldReInitChatSession) {
    sendLogMessage('Reinitializing chat session due to idle timeout', 'info');
    localStorage.clear();
  }

  createHTTPInstance(URL, configToContextValue.botId);

  renderRootForContainerId(
    'conversed-chat-widget-1848',
    <ConfigProvider value={configToContextValue}>
      <App config={config} />
    </ConfigProvider>,
  );
};

type EventHandlerParams = {
  CONVERSED_READY: {
    config?: Config;
    configId?: string;
    metadata: Metadata;
  };
  CONVERSED_LOCATION: { location: Location; error: LocationError };
  CONVERSED_EVENT: { type: 'new-webclient'; data: { id: string } };
};

const eventHandlers = {
  CONVERSED_READY: async ({
    config,
    configId,
    metadata,
  }: EventHandlerParams['CONVERSED_READY']) => {
    const configToUse = await getConfigToUse({
      configFromScript: config,
      configId,
      metadata,
    });
    return render(configToUse);
  },
  CONVERSED_LOCATION: ({
    location,
    error,
  }: EventHandlerParams['CONVERSED_LOCATION']) =>
    location?.longitude && location?.latitude
      ? geoLocationService.sendLocation(location)
      : geoLocationService.sendLocationError(error),
  CONVERSED_EVENT: ({ type, data }: EventHandlerParams['CONVERSED_EVENT']) =>
    eventHandlerService.receiveBroadcast(type, data),
};

const setupPostMessageListeners = () => {
  window.addEventListener('message', ({ data }: MessageEvent) => {
    // TODO get ALLOWED_ORIGINS from backend based on subdomain
    const handler = eventHandlers[data.eventType?.toString()];
    if (handler) {
      handler(data);
    }
  });
};

const init = (): unknown => {
  const isInIFrame = window.location !== window.parent.location;
  if (isInIFrame) {
    sendLogMessage('Loading chat interface', 'info');
    return setupPostMessageListeners();
  }

  const isInDevelopment = NODE_ENV !== 'production';
  if (isInDevelopment) {
    return render(getConfigToUseFromQueryString());
  }
  // eslint-disable-next-line no-console
  return console.error('This site must be loaded in IFRAME');
};

init();
